<template>
  <section class="memoriam" aria-labelledby="memoriam-heading">
    <div class="container">
      <p class="memoriam-dates">September 11, 2001</p>
      <h2 id="memoriam-heading" class="memoriam-title">
        <span class="memoriam-number">{{ yearsSince }}</span>
        <span class="memoriam-words">Years<br><em>Forever Missing Cathy</em></span>
      </h2>
      <div class="memoriam-rule"></div>
      <p class="memoriam-lead">
        Wellsville still remembers. On September 11, 2026, the 25th anniversary of the attacks,
        family and friends gathered at the Wellsville 9/11 Memorial on Lisbon Street to lay a new wreath in Cathy's honor.
      </p>

      <div
        id="memoriam-strip"
        ref="strip"
        class="memoriam-strip"
        role="region"
        aria-label="Photos and video from the 2026 wreath laying"
        tabindex="0"
        @scroll.passive="onStripScroll"
      >
        <figure class="tile tile-video">
          <video
            :src="video + '#t=0.5'"
            controls
            playsinline
            preload="metadata"
            aria-label="Video of the 2026 wreath laying at the Wellsville 9/11 Memorial"
            aria-describedby="memoriam-video-desc"
          ></video>
          <figcaption id="memoriam-video-desc" class="visually-hidden">
            A 45 second video of family members laying a red, white and blue wreath beside Cathy's memorial stone
            at the Wellsville 9/11 Memorial on Lisbon Street, September 11, 2026. The audio is casual conversation while the wreath is placed, with no narration.
          </figcaption>
        </figure>
        <figure v-for="(photo, i) in photos" :key="photo.src" class="tile" :style="{ '--ratio': photo.ratio }">
          <button type="button" class="tile-button" @click="open(i, $event)">
            <img :src="photo.src" :alt="photo.alt" loading="lazy">
            <span class="visually-hidden">(opens larger view)</span>
          </button>
        </figure>
      </div>

      <div class="memoriam-nav">
        <button type="button" class="nav-arrow" aria-controls="memoriam-strip" aria-label="Previous" :disabled="current === 0" @click="step(-1)">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="nav-count" aria-live="polite">{{ current + 1 }} / {{ photos.length + 1 }}</span>
        <button type="button" class="nav-arrow" aria-controls="memoriam-strip" aria-label="Next" :disabled="current === photos.length" @click="step(1)">
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
      <p class="memoriam-caption"><span>The wreath laying</span> &middot; Wellsville 9/11 Memorial, <span>Lisbon Street &middot; 2026</span></p>

      <p class="memoriam-closing">&ldquo;We Will Never Forget&rdquo;</p>
    </div>

    <div
      v-if="active !== null"
      ref="dialog"
      class="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      @click="close"
      @keydown="onDialogKey"
    >
      <button ref="closeButton" type="button" class="lightbox-btn lightbox-close" aria-label="Close photo viewer" @click.stop="close">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
      <button type="button" class="lightbox-btn lightbox-prev" aria-label="Previous photo" @click.stop="show(-1)">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
      <figure class="lightbox-figure" @click.stop>
        <img :src="photos[active].src" :alt="photos[active].alt">
        <figcaption>{{ photos[active].alt }} <span class="lightbox-count">{{ active + 1 }} / {{ photos.length }}</span></figcaption>
      </figure>
      <button type="button" class="lightbox-btn lightbox-next" aria-label="Next photo" @click.stop="show(1)">
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
  </section>
</template>

<script>
import video from '@/assets/2026/IMG_7213.mp4'
import wreath from '@/assets/2026/IMG_7212.webp'
import groupOne from '@/assets/2026/IMG_7217.webp'
import groupTwo from '@/assets/2026/IMG_7220.webp'

// Full years since September 11, 2001; ticks over every September 11th.
function yearsSinceAttacks() {
  const now = new Date()
  const anniversaryPassed = now.getMonth() > 8 || (now.getMonth() === 8 && now.getDate() >= 11)
  return now.getFullYear() - 2001 - (anniversaryPassed ? 0 : 1)
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

export default {
  name: 'MemoriamSection',
  data() {
    return {
      video,
      yearsSince: yearsSinceAttacks(),
      current: 0,
      active: null,
      returnFocus: null,
      photos: [
        { src: wreath, ratio: 960 / 1024, alt: "A red, white and blue wreath beside Cathy's memorial stone, which reads \"We Will Never Forget, In Loving Memory of Cathy Salter\", with yellow roses in a white vase." },
        { src: groupOne, ratio: 960 / 1280, alt: "Two family members stand on either side of Cathy's memorial stone and the new wreath, with the American flag at half-staff and the Lisbon Street murals behind them." },
        { src: groupTwo, ratio: 960 / 1280, alt: "Family members and a firefighter in dress uniform stand together at Cathy's memorial stone beside the new wreath." }
      ]
    }
  },
  methods: {
    tiles() {
      return Array.from(this.$refs.strip.children)
    },
    // Arrow buttons: centre the previous / next tile in the strip
    step(dir) {
      const strip = this.$refs.strip
      const target = this.tiles()[this.current + dir]
      if (!target) return
      strip.scrollTo({
        left: target.offsetLeft - strip.offsetLeft - (strip.clientWidth - target.clientWidth) / 2,
        behavior: reducedMotion() ? 'auto' : 'smooth'
      })
    },
    // Keep the counter in sync with swipes by picking the tile nearest the strip's centre
    onStripScroll() {
      const strip = this.$refs.strip
      const centre = strip.offsetLeft + strip.scrollLeft + strip.clientWidth / 2
      let best = 0
      let bestDist = Infinity
      this.tiles().forEach((tile, i) => {
        const dist = Math.abs(tile.offsetLeft + tile.clientWidth / 2 - centre)
        if (dist < bestDist) {
          best = i
          bestDist = dist
        }
      })
      this.current = best
    },
    open(index, event) {
      this.returnFocus = event.currentTarget
      this.active = index
      document.body.style.overflow = 'hidden'
      this.$nextTick(() => this.$refs.closeButton.focus())
    },
    close() {
      this.active = null
      document.body.style.overflow = ''
      if (this.returnFocus) this.returnFocus.focus()
    },
    show(dir) {
      this.active = (this.active + dir + this.photos.length) % this.photos.length
    },
    onDialogKey(e) {
      if (e.key === 'Escape') {
        this.close()
      } else if (e.key === 'ArrowLeft') {
        this.show(-1)
      } else if (e.key === 'ArrowRight') {
        this.show(1)
      } else if (e.key === 'Tab') {
        // Keep keyboard focus inside the viewer while it is open
        const buttons = Array.from(this.$refs.dialog.querySelectorAll('button'))
        const i = buttons.indexOf(document.activeElement)
        e.preventDefault()
        buttons[(i + (e.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus()
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.memoriam {
  background: #0b0b0b;
  color: #e9e6e1;
  padding: 4.5rem 0 4rem;
  text-align: center;
}

.memoriam-dates {
  font-size: .8rem;
  letter-spacing: .3em;
  text-transform: uppercase;
  color: #a39e96;
  margin-bottom: 1.5rem;
}

.memoriam-caption span {
  white-space: nowrap;
}

.memoriam-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  margin: 0;
  font-weight: 400;
}
.memoriam-number {
  font-size: clamp(5rem, 14vw, 8.5rem);
  line-height: .85;
  color: #fff;
  font-variant-numeric: lining-nums;
}
.memoriam-words {
  text-align: left;
  font-size: clamp(1.4rem, 3.5vw, 2.25rem);
  line-height: 1.2;
  color: #fff;
}
.memoriam-words em {
  color: #c98b8b;
}

.memoriam-rule {
  width: 64px;
  height: 2px;
  background: rgb(150, 20, 20);
  margin: 2rem auto;
}

.memoriam-lead {
  max-width: 640px;
  margin: 0 auto 3rem;
  font-size: 1.15rem;
  line-height: 1.7;
  color: #cfcac3;
}

/* Every tile keeps its own aspect ratio; flex-grow by that ratio
   makes all tiles share one height so photos and video read as one row. */
.memoriam-strip {
  --ratio: 1;
  display: flex;
  gap: .75rem;
  align-items: flex-start;
}
.memoriam-strip:focus-visible {
  outline: 2px solid #c98b8b;
  outline-offset: 4px;
  border-radius: .375rem;
}
.tile {
  flex: var(--ratio) 1 0;
  aspect-ratio: var(--ratio);
  margin: 0;
  overflow: hidden;
  border-radius: .375rem;
  background: #1a1a1a;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, .08);
}
.tile-video {
  --ratio: 0.5625;
}
.tile video,
.tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tile-button {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: zoom-in;
}
.tile-button img {
  transition: transform .4s ease, filter .4s ease;
  filter: saturate(.92);
}
.tile-button:hover img,
.tile-button:focus-visible img {
  transform: scale(1.03);
  filter: saturate(1);
}
.tile-button:focus-visible {
  outline: 2px solid #c98b8b;
  outline-offset: -2px;
}

/* Arrow controls: only shown on phones, where the strip scrolls */
.memoriam-nav {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  margin-top: 1rem;
}
.nav-arrow {
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, .25);
  border-radius: 50%;
  background: transparent;
  color: #fff;
}
.nav-arrow:disabled {
  opacity: .3;
}
.nav-arrow:focus-visible {
  outline: 2px solid #c98b8b;
  outline-offset: 2px;
}
.nav-count {
  min-width: 3.5rem;
  font-size: .85rem;
  letter-spacing: .15em;
  color: #cfcac3;
  font-variant-numeric: tabular-nums;
}

.memoriam-caption {
  margin-top: 1rem;
  font-size: .85rem;
  color: #8a857e;
}

.memoriam-closing {
  margin: 2.75rem 0 0;
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-size: 1.5rem;
  color: #fff;
}

/* Phones: swipeable strip with a shared fixed height */
@media (max-width: 767px) {
  .memoriam {
    padding: 3rem 0;
  }
  .memoriam-dates {
    letter-spacing: .2em;
  }
  .memoriam-strip {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin: 0 -12px;
    padding: 0 12px 4px;
  }
  .tile {
    flex: none;
    height: min(62vh, 440px);
    scroll-snap-align: center;
  }
  .memoriam-nav {
    display: flex;
  }
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 4.5rem 1.5rem;
  background: rgba(0, 0, 0, .98);
  cursor: zoom-out;
}
.lightbox-figure {
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
}
.lightbox-figure img {
  max-width: 100%;
  max-height: calc(100vh - 11rem);
  border-radius: .375rem;
}
.lightbox-figure figcaption {
  max-width: 640px;
  margin-top: 1rem;
  font-size: .9rem;
  line-height: 1.5;
  color: #cfcac3;
}
.lightbox-count {
  display: block;
  margin-top: .35rem;
  letter-spacing: .15em;
  color: #8a857e;
}
.lightbox-btn {
  position: absolute;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, .12);
  color: #fff;
  font-size: 1.15rem;
}
.lightbox-btn:focus-visible {
  outline: 2px solid #c98b8b;
  outline-offset: 2px;
}
.lightbox-close {
  top: 1rem;
  right: 1rem;
}
.lightbox-prev,
.lightbox-next {
  top: 50%;
  transform: translateY(-50%);
}
.lightbox-prev {
  left: 1rem;
}
.lightbox-next {
  right: 1rem;
}
@media (max-width: 767px) {
  .lightbox {
    padding: 4rem .75rem 5rem;
  }
  .lightbox-prev,
  .lightbox-next {
    top: auto;
    bottom: 1rem;
    transform: none;
  }
  .lightbox-prev {
    left: calc(50% - 56px);
  }
  .lightbox-next {
    right: calc(50% - 56px);
  }
}

.visually-hidden {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tile-button img {
    transition: none;
  }
  .tile-button:hover img,
  .tile-button:focus-visible img {
    transform: none;
  }
}
</style>
